import { type FC, type ReactNode, useRef } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import ChevronLeft from 'dibs-icons/exports/ChevronLeft.ts';
import ChevronRight from 'dibs-icons/exports/ChevronRight.ts';

import { ScrollContainer } from 'dibs-scroll-carousel/exports/ScrollContainer.ts';
import { useInternalScrollCarousel } from 'dibs-scroll-carousel/exports/InternalScrollCarouselContext.ts';

import { type TileSize } from '../constants.ts';

import dibsCss from 'dibs-css';
import styles from '../styles/styles.scss';
import { useVisibilityTracker } from 'dibs-visibility-tracker/exports/useVisibilityTracker.ts';

const commonClasses = classnames(dibsCss.absolute, dibsCss.zBump, dibsCss.top50);
const swiperClasses = classnames(
    commonClasses,
    styles.swipeIndicator,
    dibsCss.right0,
    dibsCss.textBodySmallHeavy,
    dibsCss.textNoir,
    dibsCss.flex,
    dibsCss.itemsCenter,
    dibsCss.hLarge,
    dibsCss.plSmaller,
    dibsCss.prXxsmall
);

const arrowClasses = classnames(
    commonClasses,
    styles.arrowLink,
    dibsCss.flex,
    dibsCss.justifyCenter,
    dibsCss.itemsCenter
);

export type Props = {
    children: ReactNode[];
    size: TileSize;
    showArrows: boolean;
    showSwiper: boolean;
    setIsSwiperDismissed: (isSwiperDismissed: boolean) => void;
};

export const ProductCarousel: FC<Props> = ({
    children,
    size,
    showArrows,
    showSwiper,
    setIsSwiperDismissed,
}) => {
    const { currentIndex, setCurrentIndex } = useInternalScrollCarousel();
    const swiperRef = useRef<HTMLDivElement>(null);

    useVisibilityTracker({
        elementRef: swiperRef,
        watchAfterFirstVisible: true,
        isEnabled: showSwiper,
        onVisibilityChange: ({ boundingClientRect, disconnect }) => {
            if (boundingClientRect?.top < 0) {
                // hide swipe indicator if user scrolled past it
                setIsSwiperDismissed(true);
                disconnect();
            }
        },
    });

    return (
        <div className={classnames(dibsCss.wFull, dibsCss.hFull, dibsCss.relative)}>
            {showArrows && (
                <>
                    <Link
                        className={classnames(arrowClasses, dibsCss.left0, {
                            [styles.arrowLinkLarge]: size === 'large',
                        })}
                        onClick={() => setCurrentIndex(currentIndex - 1)}
                    >
                        <ChevronLeft variation="24" dataTn="arrow-left" />
                    </Link>

                    <Link
                        className={classnames(arrowClasses, dibsCss.right0, {
                            [styles.arrowLinkLarge]: size === 'large',
                        })}
                        onClick={() => setCurrentIndex(currentIndex + 1)}
                    >
                        <ChevronRight variation="24" dataTn="arrow-right" />
                    </Link>
                </>
            )}
            {showSwiper && (
                <div className={swiperClasses} ref={swiperRef}>
                    <FormattedMessage defaultMessage="Swipe" id="productTile.swipeIndicatorText" />
                    <ChevronRight variation="20" />
                </div>
            )}
            <ScrollContainer>{children}</ScrollContainer>
        </div>
    );
};
